import { Card, Typography, List, ListItem, Avatar, Box } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatCurrency } from '../../utils/formatCurrency';
import { capitalize } from 'lodash';
import { offers as OfferType } from '@prisma/client';

interface OfferTimelineProps {
  offers: OfferType[];
}

const OfferTimeline: React.FC<OfferTimelineProps> = ({ offers }) => {
  // "Offer History" is a chronology, and this renders in array order while
  // deriving both the sequence badge (`offers.length - idx`) and the
  // highlighted entry (`idx === 0`) from position. Callers hand it whatever
  // order suits them — selectOffersByListingIdAndUserId sorts by stage so the
  // live offer lands at [0] for the offer card — which would otherwise show an
  // older accepted offer above a newer rescinded one and number it higher.
  // Sorting a copy here keeps the history correct for every caller instead of
  // making each one pass a second, differently-ordered array.
  const chronological = [...offers].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Offer History</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Card sx={{ boxShadow: 'none' }}>
          <List disablePadding>
            {chronological.map((o, idx) => (
              <ListItem
                key={o.id}
                sx={{
                  mb: 2,
                  px: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ minWidth: 150 }}
                >
                  {new Date(o.createdAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: idx === 0 ? 'primary.main' : 'grey.400',
                    width: 32,
                    height: 32,
                    fontSize: 16,
                    fontWeight: 700,
                    mx: 1,
                  }}
                >
                  {chronological.length - idx}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatCurrency(o.price)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Status: {capitalize(o.status)}
                  </Typography>
                  {o.counter && (
                    <Typography variant="body2" color="warning.main">
                      Counter: {o.counter}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        </Card>
      </AccordionDetails>
    </Accordion>
  );
};

export default OfferTimeline;
